import React from 'react';
import { Form, Button, Collapse } from 'react-bootstrap';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { Paging } from 'utils/miscellaneous';

export type MedicineType = '外' | '注' | '内' | '歯';

interface PriceShowSearchFormProps {
  isSearchCollapsed: boolean;
  toggleSearchCollapse: () => void;
  medicineName: string;
  setMedicineName: (value: string) => void;
  capacity: string;
  setCapacity: (value: string) => void;
  manufacturer: string;
  setManufacturer: (value: string) => void;
  genericName: string;
  setGenericName: (value: string) => void;
  janCode: string;
  setJanCode: (value: string) => void;
  yjCode: string;
  setYjCode: (value: string) => void;
  gs1Code: string;
  setGs1Code: (value: string) => void;
  medicineCategory: string;
  setMedicineCategory: (value: string) => void;
  setSearchOperation: (value: string) => void;
  paging: Paging;
  setPaging: (paging: Paging) => void;
  handleSearchButtonClick: () => void;
  medicineTypes: MedicineType[];
  onTypeChange: (medicineType: MedicineType, on: boolean) => void;
  excludeProcessed: boolean;
  setExcludeProcessed: (value: boolean) => void;
  hasPurchaseHistory: boolean;
  setHasPurchaseHistory: (value: boolean) => void;
  hasNoPurchaseHistory: boolean;
  setHasNoPurchaseHistory: (value: boolean) => void;
}

/**
 * 価格表の検索条件フォーム（D4 step2: PriceShowList から抽出）。
 * 状態は親が保持し props で受け渡す。ロジック・挙動は分離前と不変。
 */
export const PriceShowSearchForm: React.FC<PriceShowSearchFormProps> = ({
  isSearchCollapsed,
  toggleSearchCollapse,
  medicineName,
  setMedicineName,
  capacity,
  setCapacity,
  manufacturer,
  setManufacturer,
  genericName,
  setGenericName,
  janCode,
  setJanCode,
  yjCode,
  setYjCode,
  gs1Code,
  setGs1Code,
  medicineCategory,
  setMedicineCategory,
  setSearchOperation,
  paging,
  setPaging,
  handleSearchButtonClick,
  medicineTypes,
  onTypeChange,
  excludeProcessed,
  setExcludeProcessed,
  hasPurchaseHistory,
  setHasPurchaseHistory,
  hasNoPurchaseHistory,
  setHasNoPurchaseHistory,
}) => {
  return (
    <div className="search bg-light rounded">
      {/* 検索条件ヘッダー（常に表示。クリック/Enter/Spaceで折り畳み切替） */}
      <div
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={toggleSearchCollapse}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleSearchCollapse();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!isSearchCollapsed}
        style={{ cursor: 'pointer' }}
      >
        <h5 className="mb-0">検索条件</h5>
        <div className="d-flex align-items-center">
          {isSearchCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
        </div>
      </div>

      {/* 折り畳み可能な検索条件エリア */}
      <Collapse in={!isSearchCollapsed}>
        <div className="p-3">
          <div className="row g-3 mb-3">
            <div className="col">
              <Form.Control
                type="text"
                placeholder="品名"
                value={medicineName}
                onChange={(e) => setMedicineName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col">
              <Form.Control
                type="text"
                placeholder="規格容量"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col">
              <Form.Control
                type="text"
                placeholder="販売元名"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col">
              <Form.Control
                type="text"
                placeholder="一般名"
                value={genericName}
                onChange={(e) => setGenericName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col-2 d-flex align-items-center">
              <div style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                <Form.Check
                  type="radio"
                  id="search-operator-and"
                  label=""
                  name="searchOperator"
                  defaultChecked
                  onChange={() => {
                    setSearchOperation('and');
                    setPaging({ page: 0, pageSize: paging.pageSize });
                  }}
                />
                <label htmlFor="search-operator-and" className="ms-2">
                  and
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                <Form.Check
                  type="radio"
                  id="search-operator-or"
                  label=""
                  name="searchOperator"
                  onChange={() => {
                    setSearchOperation('or');
                    setPaging({ page: 0, pageSize: paging.pageSize });
                  }}
                />
                <label htmlFor="search-operator-or" className="ms-2">
                  or
                </label>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <Form.Control
                type="text"
                placeholder="JANコード"
                value={janCode}
                onChange={(e) => setJanCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col">
              <Form.Control
                type="text"
                placeholder="YJコード"
                value={yjCode}
                onChange={(e) => setYjCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col">
              <Form.Control
                type="text"
                placeholder="GS1コード"
                value={gs1Code}
                onChange={(e) => setGs1Code(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col">
              <Form.Control
                type="text"
                placeholder="薬効分類"
                value={medicineCategory}
                onChange={(e) => setMedicineCategory(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchButtonClick()}
                className="rounded-pill"
              />
            </div>
            <div className="col-2">
              <Button className="w-100 bg-success rounded-pill" onClick={handleSearchButtonClick}>
                検索実行
              </Button>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center mt-3">
            <Form.Check
              inline
              id="medicine-type-gai"
              label="外"
              checked={medicineTypes.includes('外')}
              onChange={(e) => onTypeChange('外', e.target.checked)}
              className="me-3"
            />
            <Form.Check
              inline
              id="medicine-type-chu"
              label="注"
              checked={medicineTypes.includes('注')}
              onChange={(e) => onTypeChange('注', e.target.checked)}
              className="me-3"
            />
            <Form.Check
              inline
              id="medicine-type-nai"
              label="内"
              checked={medicineTypes.includes('内')}
              onChange={(e) => onTypeChange('内', e.target.checked)}
              className="me-3"
            />
            <Form.Check
              inline
              id="medicine-type-shi"
              label="歯"
              checked={medicineTypes.includes('歯')}
              onChange={(e) => onTypeChange('歯', e.target.checked)}
              className="me-4"
            />
            <Form.Check
              inline
              id="exclude-processed"
              label="経過措置を対象外にする"
              checked={excludeProcessed}
              onChange={(e) => {
                setExcludeProcessed(e.target.checked);
                setPaging({ page: 0, pageSize: paging.pageSize });
              }}
              className="me-4"
            />
            <Form.Check
              inline
              id="has-purchase-history"
              label="購入実績有"
              checked={hasPurchaseHistory}
              onChange={(e) => {
                setHasPurchaseHistory(e.target.checked);
                setPaging({ page: 0, pageSize: paging.pageSize });
              }}
              className="me-4"
            />
            <Form.Check
              inline
              id="has-no-purchase-history"
              label="購入実績無"
              checked={hasNoPurchaseHistory}
              onChange={(e) => {
                setHasNoPurchaseHistory(e.target.checked);
                setPaging({ page: 0, pageSize: paging.pageSize });
              }}
              className="me-4"
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
};
